import React from 'react';

/**
 * Shared pieces for the Logging pages (System Logs, Security Audit Log,
 * Audit Logs, Impersonation Activity).
 *
 * All four follow the same shape: pull the table in one request, hand every row
 * to TanTable (which virtualizes and does sort / filter / search / export on the
 * client), and put anything that needs room into a slide-out drawer. Row cells
 * must stay single-line — the virtualizer assumes a fixed row height, and
 * multi-line cells make virtualized rows overlap.
 */

/**
 * Matches AUDIT_LOGS_MAX_LIMIT on the server.
 *
 * These tables grow without a routine clear (security_audit_log reached 2.6M
 * rows), so this is a real ceiling rather than a formality: the pages sort
 * newest-first and show a truncation banner when the server's `total` exceeds
 * what came back, so a capped result reads as capped.
 */
export const LOG_FETCH_ALL_LIMIT = 50000;

/**
 * Auto-refresh interval. Pulling a whole table is far heavier than the old
 * 50-row page, so this is deliberately slower than the 5-10s the paginated
 * versions used.
 */
export const LOG_AUTO_REFRESH_MS = 30000;

/** A label/value pair inside a drawer's metadata <dl>. */
export const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <dt className="text-xs text-muted">{label}</dt>
    <dd className="text-sm text-heading break-all">{children}</dd>
  </div>
);

/** Placeholder for an absent value in a drawer field. */
export const NONE = <span className="italic text-gray-500">(none)</span>;

/** Pretty-print a JSON-ish value that may arrive as a string or an object. */
export const formatJsonMaybe = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};
